import {
  TABLE_SORTER_TOOLTIP,
  LIST_CREATE_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty } from '../../../shared/ui/quiet-states.js';
// Platform admin workspaces screen (archetype: Master-Detail, cf.
// TemplatesPage). Differences from tenant screens: server-side pagination,
// the right panel is a read-only detail view with block/unblock (the only
// mutations — workspace data itself is never edited here), and blocked
// workspaces stay visible so they can be unblocked.
import { useMemo, useState, type ReactElement } from 'react';
import { Badge, Button, Table, Tooltip, Typography } from 'antd';
import type { ColumnType, TablePaginationConfig } from 'antd/es/table/interface';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { FilterBar, FilterSearch } from '../../../shared/ui/FilterBar.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { formatDate, formatAdminSpend } from '../lib/format.js';
import { useAdminPlans } from '../hooks/useAdminPlans.js';
import { useAdminWorkspaceDetail, useAdminWorkspaces } from '../hooks/useAdminWorkspaces.js';
import { useAdminWorkspacesMutations } from '../hooks/useAdminWorkspacesMutations.js';
import { WorkspaceDetailPanel } from '../components/WorkspaceDetailPanel.js';
import type { AdminWorkspaceListItem } from '../api/admin.client.js';

const { Text } = Typography;

const DEFAULT_PAGE_SIZE = 20;

export function AdminWorkspacesPage(): ReactElement {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);

  const workspacesQuery = useAdminWorkspaces({
    page,
    pageSize,
    ...(activeQuery.trim().length > 0 ? { search: activeQuery.trim() } : {}),
  });
  const rows = useMemo(() => workspacesQuery.data?.workspaces ?? [], [workspacesQuery.data]);
  const total = workspacesQuery.data?.total ?? 0;
  const { blockMutation, unblockMutation } = useAdminWorkspacesMutations();
  const anyMutationPending = blockMutation.isPending || unblockMutation.isPending;

  // Master-Detail selection: an explicit click wins; otherwise the first row
  // of the current page is the detail subject (cf. TemplatesPage).
  const activeWorkspaceId = selectedWorkspaceId ?? (rows.length > 0 ? (rows[0]?.id ?? null) : null);
  const detailQuery = useAdminWorkspaceDetail(activeWorkspaceId);

  // Plan configs: the panel shows the limits of the workspace's plan next to
  // the plan code (owner decision 2026-10-20: limits are admin-managed data).
  const plansQuery = useAdminPlans();
  const planByCode = useMemo(() => {
    const map = new Map<string, NonNullable<typeof plansQuery.data>[number]>();
    for (const plan of plansQuery.data ?? []) {
      map.set(plan.code, plan);
    }
    return map;
  }, [plansQuery.data]);
  const detailPlanConfig =
    detailQuery.data === undefined
      ? null
      : (planByCode.get(detailQuery.data.workspace.plan) ?? null);

  const columns = useMemo<Array<ColumnType<AdminWorkspaceListItem>>>(
    () => [
      {
        title: t('crm.admin.workspaces.col_name'),
        key: 'name',
        render: (_: unknown, workspace) => (
          <div className="flex min-w-0 flex-col gap-0.5">
            <Text strong style={{ color: 'var(--ant-color-text)', fontSize: 13 }}>
              {workspace.name}
            </Text>
            <Text className="font-mono" type="secondary" style={{ fontSize: 11 }} ellipsis>
              {workspace.slug}
            </Text>
          </div>
        ),
      },
      {
        title: t('crm.admin.workspaces.col_owner'),
        key: 'owner',
        width: 200,
        ellipsis: true,
        render: (_: unknown, workspace) =>
          workspace.owner ? (
            <Tooltip title={workspace.owner.email}>
              <Text className="font-mono" style={{ fontSize: 12 }} ellipsis>
                {workspace.owner.email}
              </Text>
            </Tooltip>
          ) : (
            <Text disabled style={{ fontSize: 12 }}>
              —
            </Text>
          ),
      },
      {
        title: t('crm.admin.workspaces.col_members'),
        dataIndex: 'activeMembers',
        key: 'activeMembers',
        width: 100,
        align: 'right',
        render: (value: number) => (
          <Text className="font-semibold font-mono" style={{ fontSize: 12 }}>
            {value}
          </Text>
        ),
      },
      {
        title: t('crm.admin.workspaces.col_deals'),
        dataIndex: 'deals',
        key: 'deals',
        width: 90,
        align: 'right',
        render: (value: number) => (
          <Text className="font-mono" style={{ fontSize: 12 }}>
            {value}
          </Text>
        ),
      },
      {
        title: t('crm.admin.workspaces.col_spend'),
        dataIndex: 'spendRub',
        key: 'spendRub',
        width: 130,
        align: 'right',
        render: (value: string) => (
          <Text className="font-mono" style={{ fontSize: 12 }}>
            {formatAdminSpend(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.workspaces.col_kind'),
        key: 'kind',
        width: 110,
        render: (_: unknown, workspace) => (
          <EntityStatusChip style={{ marginInlineEnd: 0, fontSize: 11 }}>
            {workspace.kind === 'personal'
              ? t('crm.admin.workspaces.kind_personal')
              : t('crm.admin.workspaces.kind_business')}
          </EntityStatusChip>
        ),
      },
      {
        title: t('crm.admin.workspaces.col_status'),
        key: 'status',
        width: 120,
        render: (_: unknown, workspace) =>
          workspace.isBlocked ? (
            <Badge
              status="error"
              text={
                <span style={{ fontSize: 11 }}>{t('crm.admin.workspaces.status_blocked')}</span>
              }
            />
          ) : (
            <Badge
              status="success"
              text={<span style={{ fontSize: 11 }}>{t('crm.admin.workspaces.status_active')}</span>}
            />
          ),
      },
      {
        title: t('crm.admin.workspaces.col_created'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 120,
        render: (value: string) => (
          <Text style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
            {formatDate(value)}
          </Text>
        ),
      },
    ],
    [t],
  );

  const pagination = useMemo<TablePaginationConfig>(
    () => ({
      current: page,
      pageSize,
      total,
      showSizeChanger: true,
      pageSizeOptions: [20, 30, 50].map(String),
      onChange: (nextPage, nextPageSize) => {
        setPage(nextPage);
        setPageSize(nextPageSize);
      },
    }),
    [page, pageSize, total],
  );

  function applySearch(value: string): void {
    setActiveQuery(value);
    setPage(1);
  }

  if (workspacesQuery.isError) {
    return (
      <ErrorState
        title={t('crm.admin.workspaces.load_error')}
        error={workspacesQuery.error}
        onRetry={() => void workspacesQuery.refetch()}
      />
    );
  }

  return (
    <div className="animate-fade-in flex flex-col">
      <PageHeader
        title={t('crm.admin.workspaces.title')}
        subtitle={t('crm.admin.workspaces.subtitle', { visible: rows.length, total })}
        actions={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void workspacesQuery.refetch()}
            loading={workspacesQuery.isFetching}
            aria-label={t('crm.admin.workspaces.reload')}
            style={LIST_CREATE_BUTTON_STYLE}
          >
            {t('crm.admin.workspaces.reload')}
          </Button>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <FilterBar
          search={
            <FilterSearch
              aria-label={t('crm.admin.workspaces.search_label')}
              placeholder={t('crm.admin.workspaces.search_placeholder')}
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              onPressEnter={applySearch}
            />
          }
        />

        <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row lg:items-start">
          <div className="min-h-0 flex-1 lg:self-stretch" style={LIST_SURFACE_STYLE}>
            <Table<AdminWorkspaceListItem>
              showSorterTooltip={TABLE_SORTER_TOOLTIP}
              size="small"
              rowKey="id"
              columns={columns}
              dataSource={rows}
              loading={workspacesQuery.isLoading}
              pagination={pagination}
              onRow={(workspace) => ({
                onClick: () => setSelectedWorkspaceId(workspace.id),
                style: {
                  cursor: 'pointer',
                  background:
                    activeWorkspaceId === workspace.id ? 'var(--ant-color-primary-bg)' : undefined,
                },
              })}
              locale={{
                emptyText: (
                  <QuietEmpty
                    text={
                      activeQuery.trim().length > 0
                        ? t('crm.admin.workspaces.empty_search')
                        : t('crm.admin.workspaces.empty')
                    }
                  />
                ),
              }}
              data-testid="admin-workspaces-table"
            />
          </div>

          <aside className="w-full shrink-0 lg:w-[380px] xl:w-[420px]">
            <WorkspaceDetailPanel
              detail={detailQuery.data ?? null}
              isLoading={detailQuery.isLoading}
              pending={anyMutationPending}
              planConfig={detailPlanConfig}
              onBlock={(workspaceId, reason) =>
                blockMutation.mutateAsync(
                  reason === undefined ? { workspaceId } : { workspaceId, reason },
                )
              }
              onUnblock={(workspaceId) => unblockMutation.mutateAsync(workspaceId)}
            />
          </aside>
        </div>
      </div>
    </div>
  );
}
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
